import { useCallback, useEffect, useRef, useState } from "react";
import { GridEditModal } from "../../components/editSearch/gridEditModal.tsx";
import { servicePost } from "../../api/client.ts";
import { batchedServicePost } from "../../api/readBatch.ts";
import styles from "../../styles/formPage.module.scss";

/* ── Types ──────────────────────────────────────────────── */

interface ProductLocalized {
  ProductID: number;
  OperatorID: number;
  Language: string;
  Name: string;
  Description: string;
  JsonData: string;
  Deleted: boolean;
}

interface DbLanguage {
  iLanguageID: number;
  sLanguage: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  productId: number;
  operatorId: number;
  language: string;
}

/* ── Component ──────────────────────────────────────────── */

export function LocalizedModal({ open, onClose, productId, operatorId, language }: Props) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [deleted, setDeleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dedupe in-flight fetch by composite key so StrictMode's dev double-mount
  // doesn't fire two WebGetProductLocalized calls. Key changes when any input
  // changes; in-flight promise reused if identical.
  const inFlightRef = useRef<{ key: string; promise: ReturnType<typeof servicePost<ProductLocalized>> } | null>(null);
  useEffect(() => {
    if (!open || productId <= 0 || !language) return;
    setLoading(true);
    setError(null);
    setName("");
    setDescription("");
    setDeleted(false);
    let cancelled = false;
    const key = `${productId}|${operatorId}|${language}`;
    let promise: ReturnType<typeof servicePost<ProductLocalized>>;
    if (inFlightRef.current?.key === key) {
      promise = inFlightRef.current.promise;
    } else {
      promise = servicePost<ProductLocalized>("WebGetProductLocalized", { productId, operatorId, language });
      inFlightRef.current = { key, promise };
    }
    promise
      .then(result => {
        if (cancelled) return;
        if (!result.bError && result.oData) {
          setName(result.oData.Name || "");
          setDescription(result.oData.Description || "");
          setDeleted(result.oData.Deleted || false);
        }
      })
      .catch(() => { if (!cancelled) setError("Failed to load localization"); })
      .finally(() => {
        if (!cancelled) setLoading(false);
        if (inFlightRef.current?.promise === promise) inFlightRef.current = null;
      });
    return () => { cancelled = true; };
  }, [open, productId, operatorId, language]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    const pl: ProductLocalized = {
      ProductID: productId,
      OperatorID: operatorId,
      Language: language,
      Name: name,
      Description: description,
      JsonData: "",
      Deleted: deleted,
    };
    try {
      const result = await servicePost<boolean>("WebSaveProductLocalized", { pl });
      if (result.bError || !result.oData) {
        setError(result.sMessage ?? "Failed to save localization.");
      } else {
        onClose();
      }
    } catch {
      setError("Failed to save localization.");
    } finally {
      setSaving(false);
    }
  }, [productId, operatorId, language, name, description, deleted, onClose]);

  return (
    <GridEditModal
      open={open}
      title={`Edit Localized (Language: ${language}, OID: ${operatorId})`}
      loading={loading}
      saving={saving}
      statusMessage={error ? { type: "error", message: error } : null}
      onClose={onClose}
      onSave={handleSave}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <div className={styles.fieldRowStacked}>
          <label>Name</label>
          <input className={styles.fieldInput} value={name} onChange={e => setName(e.target.value)} />
        </div>
        <div className={styles.fieldRowStacked}>
          <label>Description</label>
          <textarea className={styles.fieldTextarea} value={description} onChange={e => setDescription(e.target.value)} rows={4} />
        </div>
        <div className={styles.fieldRowStacked}>
          <label style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
            <input type="checkbox" checked={deleted} onChange={e => setDeleted(e.target.checked)} />
            Mark as deleted
          </label>
        </div>
      </div>
    </GridEditModal>
  );
}

/* ── Language loader (cached) ──────────────────────────── */

let cachedLanguages: { id: string; label: string }[] | null = null;

export async function loadLanguages(): Promise<{ id: string; label: string }[]> {
  if (cachedLanguages) return cachedLanguages;
  const result = await batchedServicePost<DbLanguage[]>("WebGetDatabaseLanguages", {});
  if (result.bError || !result.oData) return [];
  cachedLanguages = result.oData.map(l => ({
    id: String(l.iLanguageID || "0"),
    label: l.sLanguage,
  }));
  return cachedLanguages;
}
